// Player Stats widget (Players canvas) — headline numbers for the picked player
// in their selected competition + season (consumes player + comp + season).
// POST /player-stats with a fixed metric set, grouped by player (one row).

import { usePlayerStats } from "../data/players.js";
import type { WidgetScope } from "./scope.js";
import { theme } from "../lib/theme.js";

// Fixed headline metrics — all confirmed present in the engine's definitions.
const HEADLINE: { key: string; label: string }[] = [
  { key: "appearances", label: "Apps" },
  { key: "minutes", label: "Minutes" },
  { key: "goals", label: "Goals" },
  { key: "assists", label: "Assists" },
  { key: "passes", label: "Passes" },
  { key: "key_passes", label: "Key passes" },
];
const KEYS = HEADLINE.map((m) => m.key);

function fmt(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(1);
  return v;
}

export default function StatsWidget({ scope }: { scope: WidgetScope }) {
  const { playerId, competitionId, seasonId } = scope;
  const { data, isPending, isError, error } = usePlayerStats(playerId, competitionId, seasonId, KEYS);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null) return <Msg>Pick a competition and season above.</Msg>;
  if (isPending) return <Msg>Loading stats…</Msg>;
  if (isError) return <Msg danger>Couldn't load stats. {String(error)}</Msg>;

  const row = data.rows[0];
  if (!row) return <Msg>No stats for this player in this season.</Msg>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 10 }}>
      {HEADLINE.map((m) => (
        <div key={m.key} style={{ background: theme.field, borderRadius: 8, padding: "12px" }}>
          <div
            style={{
              color: theme.ink,
              fontSize: 22,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {fmt(row[m.key])}
          </div>
          <div style={{ color: theme.inkMuted, fontSize: 12, marginTop: 2 }}>{m.label}</div>
        </div>
      ))}
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
